import { useEffect, useRef, useCallback, useSyncExternalStore } from 'react'
import { useQueryClient } from '@tanstack/react-query'

type SSEHandler = (data: unknown) => void

/**
 * Global SSE connection to /api/events.
 * Singleton — only one EventSource per browser tab.
 * Reconnects automatically on disconnect.
 */
let globalES: EventSource | null = null
let globalListeners = new Map<string, Set<SSEHandler>>()
let refCount = 0

// SSE connection state — reactive via useSyncExternalStore
let sseConnected = false
let sseStatusListeners = new Set<() => void>()

function setSseConnected(connected: boolean) {
  if (sseConnected !== connected) {
    sseConnected = connected
    for (const listener of sseStatusListeners) listener()
  }
}

function getEventSource(): EventSource {
  if (!globalES || globalES.readyState === EventSource.CLOSED) {
    globalES = new EventSource('/api/events')

    globalES.onopen = () => {
      console.log('[SSE] Connected')
      setSseConnected(true)
    }

    globalES.onerror = () => {
      console.log('[SSE] Connection error — reconnecting in 5s')
      setSseConnected(false)
      // Close to prevent browser's instant auto-reconnect (causes flicker)
      if (globalES) {
        globalES.close()
        globalES = null
      }
      // Reconnect with backoff
      setTimeout(() => {
        if (refCount > 0 && !document.hidden) {
          getEventSource()
        }
      }, 5000)
    }

    // Route named events to registered handlers
    const knownEvents = [
      'status_update', 'new_message', 'voice_ready',
      'persona_died', 'context_warning',
      'conversation_updated', 'activity_event', 'project_updated',
      'chatter_message', 'magic_box_posted', 'magic_box_folder_created',
      'table_agenda_changed', 'table_vote_changed', 'table_status_changed', 'presentation_submitted',
    ]
    for (const eventName of knownEvents) {
      globalES.addEventListener(eventName, (e: MessageEvent) => {
        const handlers = globalListeners.get(eventName)
        if (!handlers) return
        try {
          const data = JSON.parse(e.data)
          for (const handler of handlers) {
            handler(data)
          }
        } catch { /* malformed event */ }
      })
    }
  }
  return globalES
}

function subscribe(event: string, handler: SSEHandler) {
  if (!globalListeners.has(event)) {
    globalListeners.set(event, new Set())
  }
  globalListeners.get(event)!.add(handler)
}

function unsubscribe(event: string, handler: SSEHandler) {
  globalListeners.get(event)?.delete(handler)
}

/**
 * Hook: manage the global SSE connection lifecycle.
 * Mount once in App. Closes when all consumers unmount.
 *
 * IMPORTANT: Pauses SSE when the tab is hidden to stay within
 * the browser's per-domain connection limit (6 for HTTP/1.1).
 * Without this, multiple tabs exhaust all connection slots and
 * new tabs can't make ANY API requests.
 */
export function useSSEConnection() {
  useEffect(() => {
    refCount++
    getEventSource()

    // Pause SSE when tab is hidden, resume when visible
    const handleVisibility = () => {
      if (document.hidden) {
        if (globalES) {
          globalES.close()
          globalES = null
          setSseConnected(false)
        }
      } else {
        getEventSource()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      refCount--
      if (refCount <= 0 && globalES) {
        globalES.close()
        globalES = null
        globalListeners = new Map()
        refCount = 0
        setSseConnected(false)
      }
    }
  }, [])
}

/**
 * Hook: returns true when SSE is connected, false when disconnected.
 * Use to conditionally enable/disable polling fallbacks.
 */
export function useSSEConnected(): boolean {
  return useSyncExternalStore(
    (cb) => { sseStatusListeners.add(cb); return () => { sseStatusListeners.delete(cb) } },
    () => sseConnected,
  )
}

/**
 * Hook: listen to a specific SSE event.
 */
export function useSSEEvent(event: string, handler: SSEHandler) {
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  const stableHandler = useCallback((data: unknown) => {
    handlerRef.current(data)
  }, [])

  useEffect(() => {
    subscribe(event, stableHandler)
    return () => unsubscribe(event, stableHandler)
  }, [event, stableHandler])
}

/**
 * Hook: wire SSE events to TanStack Query cache invalidation.
 * Mount once in App — handles all query invalidation globally.
 */
export function useSSEQueryInvalidation() {
  const queryClient = useQueryClient()

  // new_message → invalidate message queries for that conversation
  useSSEEvent('new_message', (data: unknown) => {
    const d = data as { conversation_id?: string }
    if (d.conversation_id) {
      queryClient.invalidateQueries({ queryKey: ['messages', d.conversation_id] })
      queryClient.invalidateQueries({ queryKey: ['homeChat'] })
      queryClient.invalidateQueries({ queryKey: ['ceremonyMessages'] })
      queryClient.invalidateQueries({ queryKey: ['chatter-poll', d.conversation_id] })
      // Also refresh conversation list (unread counts, last message, etc.)
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      queryClient.invalidateQueries({ queryKey: ['communityConversations'] })
      queryClient.invalidateQueries({ queryKey: ['communityConvos'] })
    }
  })

  // status_update → invalidate persona state queries
  useSSEEvent('status_update', () => {
    queryClient.invalidateQueries({ queryKey: ['personaState'] })
  })

  // persona_died → same as status_update
  useSSEEvent('persona_died', () => {
    queryClient.invalidateQueries({ queryKey: ['personaState'] })
  })

  // conversation_updated → invalidate conversation queries
  useSSEEvent('conversation_updated', () => {
    queryClient.invalidateQueries({ queryKey: ['conversations'] })
    queryClient.invalidateQueries({ queryKey: ['communityConversations'] })
    queryClient.invalidateQueries({ queryKey: ['communityConvos'] })
  })

  // activity_event → invalidate activity feed
  useSSEEvent('activity_event', () => {
    queryClient.invalidateQueries({ queryKey: ['activity'] })
    queryClient.invalidateQueries({ queryKey: ['communityActivity'] })
  })

  // chatter_message → invalidate chatter box queries
  useSSEEvent('chatter_message', (data: unknown) => {
    const d = data as { conversation_id?: string }
    if (d.conversation_id) {
      queryClient.invalidateQueries({ queryKey: ['chatterBox', d.conversation_id] })
    }
  })

  // magic_box_posted → invalidate magic box queries so new posts appear live
  useSSEEvent('magic_box_posted', (data: unknown) => {
    const d = data as { conversation_id?: string }
    if (d.conversation_id) {
      queryClient.invalidateQueries({ queryKey: ['magicBox', d.conversation_id] })
    }
  })

  useSSEEvent('magic_box_folder_created', (data: unknown) => {
    const d = data as { conversation_id?: string }
    if (d.conversation_id) {
      queryClient.invalidateQueries({ queryKey: ['magicBoxFolders', d.conversation_id] })
    }
  })

  useSSEEvent('table_agenda_changed', (data: unknown) => {
    const d = data as { conversation_id?: string }
    if (d.conversation_id) {
      queryClient.invalidateQueries({ queryKey: ['tableState', d.conversation_id] })
    }
  })

  useSSEEvent('table_vote_changed', (data: unknown) => {
    const d = data as { conversation_id?: string }
    if (d.conversation_id) {
      queryClient.invalidateQueries({ queryKey: ['tableState', d.conversation_id] })
    }
  })

  useSSEEvent('table_status_changed', (data: unknown) => {
    const d = data as { conversation_id?: string }
    if (d.conversation_id) {
      queryClient.invalidateQueries({ queryKey: ['tableState', d.conversation_id] })
    }
  })

  useSSEEvent('presentation_submitted', (data: unknown) => {
    const d = data as { conversation_id?: string }
    if (d.conversation_id) {
      queryClient.invalidateQueries({ queryKey: ['presentations', d.conversation_id] })
      queryClient.invalidateQueries({ queryKey: ['presentationStatus', d.conversation_id] })
    }
  })

  // project_updated → invalidate project queries
  useSSEEvent('project_updated', (data: unknown) => {
    const d = data as { project_id?: string }
    queryClient.invalidateQueries({ queryKey: ['projects'] })
    if (d.project_id) {
      queryClient.invalidateQueries({ queryKey: ['project', d.project_id] })
      queryClient.invalidateQueries({ queryKey: ['projectTasks', d.project_id] })
    }
    // standaloneTasks query removed — all tasks now in projects
  })
}
